<template>
  <div>
    <div class="d-flex justify-content-between px-4">
      <h1 class="font-weight-semibold">Cytology Billing</h1>
      <AddButton @click.stop.prevent="handleAddRecord" type="button" />
    </div>
    <DxGridWithSearch
      title=""
      gridName="prefixCptCodes"
      :noDataText="'No cpt pairs found.'"
      :columns="columns"
      :dataSource="cytCptStore"
    ></DxGridWithSearch>
    <div v-if="isFormOpen">
      <h2 class="mt-4">Cytology Billing Form</h2>
      <div class="row row-wrap">
        <SelectInput
          class="col col-md-6"
          v-model="formState.insuranceCategoryId"
          name="insuranceCategoryId"
          :items="insuranceCategories"
          displayExpr="displayName"
          valueExpr="id"
          label="Insurance Code"
          id="insuranceCategoryId"
        />
        <SelectInput
          class="col col-md-6"
          v-model="formState.billingTypeId"
          :items="billingTypes"
          label="Billing Type"
          id="billingTypeId"
        />
        <SelectInput
          class="col col-md-6"
          required
          v-model="formState.cytBillingModeId"
          :items="billingModes"
          label="Cytology Billing Mode"
          id="cytBillingModeId"
        />
        <SelectInput
          :items="yesNoOptions"
          class="col col-md-6"
          v-if="isBothBillingMode"
          v-model="formState.billPathologistOnlyIfAbnormalAndQcRequested"
          label="Bill Pathologist Only If Abnormal And Qc Requested"
          id="billPathologistOnlyIfAbnormalAndQcRequested"
        />
        <SelectInput
          class="col col-md-6"
          v-model="formState.screenerCptCodeId"
          :dataSource="cptCodeStore"
          searchExpr="displayName"
          label="Screener CPT Code"
          id="screenerCptCodeId"
        />
        <SelectInput
          class="col col-md-6"
          v-model="formState.pathologistCptCodeId"
          :dataSource="cptCodeStore"
          searchExpr="displayName"
          label="Pathologist CPT Code"
          id="pathologistCptCodeId"
        />
        <SelectInput
          class="col col-md-6"
          v-model="formState.additionalCptCodeId"
          :dataSource="cptCodeStore"
          searchExpr="displayName"
          label="Additional CPT Code"
          id="additionalCptCodeId"
        />
      </div>

      <div class="d-flex justify-content-end">
        <button
          type="button"
          class="btn btn-primary mr-2 d-flex align-items-center"
          @click="handleSave"
          :disabled="isLoading"
        >
          Save
          <Loader v-if="isLoading" class="ml-2" size="small" />
        </button>
        <button type="button" class="btn btn-secondary" @click="isFormOpen = false">Cancel</button>
      </div>
    </div>
  </div>
</template>

<script>
import { DropdownApi, PrefixApi, SettingsApi } from "@/services";
import AddButton from "../common/AddButton.vue";
import DxGridWithSearch from "../common/DxGridWithSearch.vue";
import DataSource from "devextreme/data/data_source";
import SelectInput from "../common/SelectInput.vue";
import { handleErrors } from "@/modules/handleErrors";
import { enumToDropDown, CytBillingModeEnum, CytTypeEnum } from "@/modules/enums";
import Loader from "../common/Loader.vue";

export default {
  name: "CytPrefixCpt",
  components: {
    DxGridWithSearch,
    AddButton,
    SelectInput,
    Loader
  },
  props: {
    prefixId: {
      type: Number,
      required: true
    },
    cytTypeId: {
      type: Number,
      required: true
    }
  },
  created() {
    SettingsApi.createSearch("/TypeCodes/Search", this.currentLab)
      .load({ filter: ["type.id", "=", 106] })
      .then(insuranceCategories => {
        this.insuranceCategories = insuranceCategories || []; //TODO - Insurance Category Type Codes need to be added
      });
  },
  data() {
    return {
      insuranceCategories: [],
      isFormOpen: false,
      defaultFormstate: {
        insuranceId: null,
        cytTypeId: null,
        billingTypeId: null,
        cytBillingModeId: null,
        billPathologistOnlyIfAbnormalAndQcRequested: null,
        screenerCptCodeId: null,
        pathologistCptCodeId: null,
        additionalCptCodeId: null
      },
      formState: {},
      isLoading: false,
      targetId: null,
      yesNoOptions: [
        {
          id: true,
          displayName: "Yes"
        },
        {
          id: false,
          displayName: "No"
        }
      ]
    };
  },
  computed: {
    billingTypes() {
      return [
        {
          id: 0,
          displayName: "Patient"
        },
        {
          id: 1,
          displayName: "Doctor"
        },
        {
          id: 2,
          displayName: "Cash Pay"
        },
        {
          id: 3,
          displayName: "No Charge"
        }
      ];
    },

    cytCptStore() {
      const store = PrefixApi.cytCptsSearch;
      return new DataSource({
        store,
        filter: ["prefixId", "=", this.prefixId]
      });
    },
    cptCodeStore() {
      return DropdownApi.searchCptCodes;
    },
    billingModes() {
      return [
        {
          id: CytBillingModeEnum.LastResultOnly,
          displayName: "Last Result Only"
        },
        {
          id: CytBillingModeEnum.BothScreenerAndPathologist,
          displayName: "Both Screener And Pathologist"
        }
      ];
    },
    isBothBillingMode() {
      return this.formState.cytBillingModeId === CytBillingModeEnum.BothScreenerAndPathologist;
    },
    columns() {
      return [
        {
          type: "buttons",
          caption: "Actions",
          buttons: [
            {
              hint: "Edit",
              icon: "edit",
              onClick: e => {
                this.handleEditRecord(e.row.data);
              }
            },
            {
              hint: "Delete",
              icon: "trash",
              onClick: e => {
                this.handleDeleteRecord(e.row.data);
              }
            }
          ]
        },
        {
          dataField: "insuranceCategoryId",
          caption: "Ins. Category",
          lookup: {
            dataSource: this.insuranceCategories,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },
        {
          dataField: "cytTypeId",
          caption: "Type",
          lookup: {
            dataSource: enumToDropDown(CytTypeEnum),
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },
        {
          dataField: "billingTypeId",
          caption: "Billing Type",
          lookup: {
            dataSource: this.billingTypes,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },
        {
          dataField: "cytBillingModeId",
          caption: " Billing Mode",
          lookup: {
            dataSource: this.billingModes,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },

        {
          dataField: "screenerCptCodeId",
          caption: "Screener Code",
          lookup: {
            dataSource: this.cptCodeStore,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },
        {
          dataField: "pathologistCptCodeId",
          caption: "Pathologist Code",
          lookup: {
            dataSource: this.cptCodeStore,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        },
        {
          dataField: "additionalCptCodeId",
          caption: "Additional Code",
          lookup: {
            dataSource: this.cptCodeStore,
            displayExpr: "displayName",
            valueExpr: "id"
          }
        }
      ];
    }
  },
  methods: {
    handleAddRecord() {
      this.isFormOpen = true;
      this.formState = { ...this.defaultFormstate };
    },
    validate(record) {
      const cpts = [
        record.screenerCptCodeId,
        record.pathologistCptCodeId,
        record.additionalCptCodeId
      ];
      // Atleast one CPT code should be selected
      if (cpts.every(cpt => cpt === null || cpt === undefined)) {
        return false;
      } else {
        throw new Error("Atleast one CPT code should be selected");
      }
    },
    async handleSave() {
      this.isLoading = true;
      const { ...formDetail } = this.formState;
      const data = {
        ...formDetail,
        prefixId: this.prefixId,
        cytTypeId: this.cytTypeId
      };
      if (this.isBothBillingMode && this.formState.billPathologistOnlyIfAbnormalAndQcRequested) {
        //! When the billing mode is set to "Both Screener And Pathologist" and the user selects "Yes" for "Bill Pathologist Only If Abnormal And Qc Requested",
        //! we need to set the cytBillingModeId to "Bill Pathologist Only If Abnormal And Qc Requested"
        data.cytBillingModeId = CytBillingModeEnum.BillPathologistOnlyIfAbnormalAndQcRequested;
      }
      try {
        if (this.targetId) {
          data.id = this.targetId;
          await PrefixApi.updateCytPrefixCpt(data);
          this.isFormOpen = false;
          this.formState = { ...this.defaultFormstate };
          this.targetId = null;
        } else {
          await PrefixApi.addCytPrefixCpt(data);
          this.isFormOpen = false;
          this.formState = { ...this.defaultFormstate };
        }
        this.cytCptStore.reload();
      } catch (error) {
        handleErrors(error);
      } finally {
        this.isLoading = false;
      }
    },
    handleEditRecord(record) {
      this.isFormOpen = true;
      if (
        record.cytBillingModeId === CytBillingModeEnum.BillPathologistOnlyIfAbnormalAndQcRequested
      ) {
        // If the record has a billing mode of "Bill Pathologist Only If Abnormal And Qc Requested",
        // we need to set the form state to reflect that the pathologist billing is only for abnormal cases and cytbilling mode is set to "Both Screener And Pathologist"
        record.billPathologistOnlyIfAbnormalAndQcRequested = true;
        record.cytBillingModeId = CytBillingModeEnum.BothScreenerAndPathologist;
      }
      this.formState = { ...record };
      this.targetId = record.id;
    },
    async handleDeleteRecord({ id }) {
      const confirmed = await window.confirm(
        "WARNING: Are you sure you want to delete this record?"
      );
      if (!confirmed) return;

      try {
        await PrefixApi.deleteCytPrefixCpt(id);
        this.cytCptStore.reload();
      } catch (error) {
        handleErrors(error);
      } finally {
        this.targetId = null;
      }
    }
  }
};
</script>

<style lang="scss" scoped></style>
